<script setup>
import { computed } from 'vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'Benchmarks vs Mercado',
  },
});

const benchmarks = computed(() => props.data?.benchmarks || {});
const currentValues = computed(() => props.data?.current_values || {});
const comparison = computed(() => props.data?.comparison || {});

const metrics = [
  {
    key: 'csat_satisfaction_rate',
    label: 'Satisfacao CSAT',
    unit: '%',
    inverse: false,
  },
  {
    key: 'sla_compliance',
    label: 'Conformidade SLA',
    unit: '%',
    inverse: false,
  },
  {
    key: 'first_response_minutes',
    label: '1a Resposta',
    unit: ' min',
    inverse: true,
  },
  {
    key: 'resolution_hours',
    label: 'Tempo Resolucao',
    unit: ' h',
    inverse: true,
  },
  {
    key: 'resolution_rate',
    label: 'Taxa Resolucao',
    unit: '%',
    inverse: false,
  },
];

function getStatusColor(status, inverse = false) {
  if (inverse) {
    return status === 'above' ? 'text-n-teal-11' : 'text-n-ruby-11';
  }
  return status === 'above' ? 'text-n-teal-11' : 'text-n-ruby-11';
}

function getStatusIcon(status, inverse = false) {
  if (inverse) {
    return status === 'above'
      ? 'i-lucide-trending-up'
      : 'i-lucide-trending-down';
  }
  return status === 'above' ? 'i-lucide-trending-up' : 'i-lucide-trending-down';
}

function getProgressWidth(current, benchmark, inverse = false) {
  if (inverse) {
    // For inverse metrics (lower is better), cap at 100% when at or below benchmark
    if (current <= benchmark) return 100;
    return Math.min((benchmark / current) * 100, 100);
  }
  return Math.min((current / benchmark) * 100, 100);
}

function getProgressColor(current, benchmark, inverse = false) {
  const ratio = inverse ? benchmark / current : current / benchmark;
  if (ratio >= 1) return 'bg-n-teal-9';
  if (ratio >= 0.8) return 'bg-n-blue-9';
  if (ratio >= 0.6) return 'bg-n-amber-9';
  return 'bg-n-ruby-9';
}
</script>

<template>
  <div class="flex flex-col rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <div class="flex items-center gap-2 mb-4">
      <span class="i-lucide-target text-lg text-n-blue-10" />
      <h3 class="text-base font-semibold text-n-slate-12">{{ title }}</h3>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center h-48">
      <Spinner size="large" />
    </div>

    <div v-else class="flex flex-col gap-4">
      <div
        v-for="metric in metrics"
        :key="metric.key"
        class="flex flex-col gap-2"
      >
        <div class="flex items-center justify-between">
          <span class="text-sm text-n-slate-12">{{ metric.label }}</span>
          <div class="flex items-center gap-2">
            <span
              class="text-sm font-bold"
              :class="
                getStatusColor(comparison[metric.key]?.status, metric.inverse)
              "
            >
              {{ currentValues[metric.key] || 0 }}{{ metric.unit }}
            </span>
            <span
              :class="[
                getStatusIcon(comparison[metric.key]?.status, metric.inverse),
                getStatusColor(comparison[metric.key]?.status, metric.inverse),
              ]"
            />
          </div>
        </div>
        <div class="relative">
          <div class="h-3 bg-n-alpha-2 rounded-full overflow-hidden">
            <div
              class="h-full rounded-full transition-all duration-500"
              :class="
                getProgressColor(
                  currentValues[metric.key],
                  benchmarks[metric.key],
                  metric.inverse
                )
              "
              :style="{
                width: `${getProgressWidth(currentValues[metric.key], benchmarks[metric.key], metric.inverse)}%`,
              }"
            />
          </div>
          <div
            class="absolute top-0 h-3 w-0.5 bg-n-slate-11"
            :style="{ left: '100%' }"
            title="Benchmark"
          />
        </div>
        <div class="flex items-center justify-between text-xs text-n-slate-11">
          <span>Seu valor: {{ currentValues[metric.key] || 0
            }}{{ metric.unit }}</span>
          <span>Benchmark: {{ benchmarks[metric.key] }}{{ metric.unit }}</span>
        </div>
      </div>

      <!-- Legend -->
      <div
        class="flex flex-wrap justify-center gap-4 pt-3 border-t border-n-weak text-xs"
      >
        <div class="flex items-center gap-1">
          <span class="w-3 h-3 rounded bg-n-teal-9" />
          <span class="text-n-slate-11">Acima do benchmark</span>
        </div>
        <div class="flex items-center gap-1">
          <span class="w-3 h-3 rounded bg-n-amber-9" />
          <span class="text-n-slate-11">Proximo</span>
        </div>
        <div class="flex items-center gap-1">
          <span class="w-3 h-3 rounded bg-n-ruby-9" />
          <span class="text-n-slate-11">Abaixo</span>
        </div>
      </div>
    </div>
  </div>
</template>
